const logFactory = require('@/libraries/DigiTickets/Logging/logFactory');
const RequestAbortedError = require('@/lib/Errors/RequestAbortedError');

/**
 * Client library for communicating with the Mellon Web ECR API using Fetch.
 * Partially generated from the Mellon documentation. It includes all routes listed in the docs,
 * some of which may not be used in the current implementation.
 */
class MellonWebECRClient {
    /**
     * @param {AppConfig} appConfig
     */
    constructor(
        appConfig,
    ) {
        this.appConfig = appConfig;

        /**
         * The base URL for the API.
         * Note that this is not set from the constructor. The loadConfig call in MellonWebECRDriver sets it.
         *
         * @type {string}
         */
        this.apiUrl = '';

        this.accessToken = null;
        this.apiKey = null;

        // On 2025-02-03 p.triantafyllos@mellongroup.com said:
        // For the calls "3.7.1 Get access token" and "3.7.2 Refresh access token" the base_url is
        // https://uat.mreceipts.com/api/
        // while for calls 3.7.3 up to 3.7.12 the base_url is
        // https://uat.mreceipts.com/api/v2.1/
        // To make this work consistently, we use /api/ as the base URL and requests that need it will add
        // the /v2.1/ as part of the endpoint.
        // Note that due to CORS issues we cannot make requests directly to Mellon from ProPoint.
        // So requests actually go to a proxy endpoint in the eInvoicing service.
        // The URL to that looks like this:
        // https://einvoicing.digitickets.co.uk/mellon-webecr/proxy/api
        // and endpoints are appended to that.

        this.log = logFactory('MellonWebECRClient');

        this.activeRequests = new Map();
    }

    /**
     * Sets the access token.
     *
     * @param {string} accessToken
     */
    setAccessToken(accessToken) {
        this.accessToken = accessToken;
    }

    /**
     * Sets the API URL.
     *
     * @param {string} apiUrl
     */
    setApiUrl(apiUrl) {
        this.apiUrl = apiUrl;
    }

    /**
     * Sets the API key.
     *
     * @param {string} apiKey
     */
    setApiKey(apiKey) {
        this.apiKey = apiKey;
    }

    /**
     * @private
     *
     * Internal fetch method to handle API requests.
     *
     * @param {string} method
     * @param {string} endpoint
     * @param {object} body
     * @param {object} headers
     * @param {AbortSignal} signal
     *
     * @returns {Promise<any>}
     * @private
     */
    async _request(method, endpoint, body = null, headers = {}, signal = null) {
        // Generate a unique ID for this request if not provided
        const requestId = Date.now().toString() + Math.random().toString(36).substring(2, 7);

        // Store the AbortController for potential external cancellation
        const controller = new AbortController();
        this.activeRequests.set(requestId, controller);
        const requestSignal = signal || controller.signal;

        const requestHeaders = {
            // Copy the headers from the params so we don't modify the original object.
            ...headers,

            // Add the header for the HTTP proxy to know where to really send the request.
            [this.appConfig.agentHttpProxyBaseUrlHeader]: this.apiUrl,
        };

        // Add content type header if not already set.
        if (!requestHeaders.hasOwnProperty('Content-Type')) {
            requestHeaders['Content-Type'] = 'application/json';
        }

        // Add authorization header if not already set, and if we have an access token.
        if (!requestHeaders.hasOwnProperty('Authorization') && this.accessToken) {
            requestHeaders.Authorization = `Bearer ${this.accessToken}`;
        }

        // Add X-Api-Key header if not already set, and if we have an API key.
        if (!requestHeaders.hasOwnProperty(MellonWebECRClient.API_KEY_HEADER) && this.apiKey) {
            requestHeaders[MellonWebECRClient.API_KEY_HEADER] = this.apiKey;
        }

        const requestOptions = {
            method,
            headers: requestHeaders,
            signal: requestSignal,
        };

        if (body !== null) {
            requestOptions.body = JSON.stringify(body);
        }

        // All requests to Mellon go via the HTTP proxy in ProPoint Agent to fix CORS headers.
        // The "Proxy-Base-Url" header (set above) is used to tell the proxy where to send the request.
        const url = `${this.appConfig.agentHttpProxyUrl}${endpoint}`;

        this.log.debug(
            requestOptions.method,
            url,
            body
        );

        try {
            const response = await fetch(url, requestOptions);

            if (!response.ok) {
                let errorBody;
                try {
                    errorBody = await response.json();
                } catch (e) {
                    errorBody = response.statusText;
                }

                throw new Error(`Request failed: ${response.status} - ${JSON.stringify(errorBody)}`);
            }

            return await response.json();
        } catch (error) {
            this.log.error('Request error', error);

            if (error.name === 'AbortError') {
                throw new RequestAbortedError();
            }

            throw error;
        } finally {
            this.activeRequests.delete(requestId);
        }
    }

    /**
     * Redeems an authentication code to get an API key.
     *
     * @param {MellonWebECRRedeemAuthCodeRequest} request
     *
     * @returns {Promise<MellonWebECRRedeemAuthCodeResponse>}
     */
    async redeemAuthCode(request) {
        return this._request(
            'POST',
            '/api/v2.1/authorization/redeem/',
            request,
            {
                // Don't send an existing API key with a request to get a new one.
                [MellonWebECRClient.API_KEY_HEADER]: null,
            }
        );
    }

    /**
     * Retrieves a list of terminals.
     *
     * @param {object} filter
     * @param {object} headers
     *
     * @returns {Promise<MellonWebECRTerminalsListResponse>}
     */
    async getTerminals(filter = {}, headers = {}) {
        const queryParams = new URLSearchParams(filter);
        return this._request(
            'GET',
            `/api/v2.1/terminal/?${queryParams}`,
            null,
            headers
        );
    }

    /**
     * Starts a new transaction.
     *
     * @param {string} terminalId
     * @param {MellonWebECRStartTransactionRequest} request
     *
     * @returns {Promise<MellonWebECRStartTransactionResponse>}
     */
    async startTransaction(terminalId, request) {
        return this._request('POST', `/api/v2.1/terminal/${terminalId}/txninit/`, request);
    }

    /**
     * Voids a transaction.
     *
     * @param {string} terminalId
     * @param {MellonWebECRVoidTransactionRequest} request
     *
     * @returns {Promise<MellonWebECRVoidTransactionResponse>}
     */
    async voidTransaction(terminalId, request) {
        return this._request('POST', `/api/v2.1/terminal/${terminalId}/txnvoid/`, request);
    }

    /**
     * Retrieves a list of transaction intents.
     *
     * @param {object} filter
     *
     * @returns {Promise<MellonWebECRTransactionIntentListResponse>}
     */
    async getTransactionIntents(filter = {}) {
        const queryParams = new URLSearchParams(filter);
        return this._request('GET', `/api/v2.1/transactionintent/?${queryParams}`);
    }

    /**
     * Retrieves details of a transaction intent.
     *
     * @param {string} intentId
     *
     * @returns {Promise<MellonWebECRTransactionIntent>}
     */
    async getTransactionIntent(intentId) {
        return this._request('GET', `/api/v2.1/transactionintent/${intentId}/`);
    }

    /**
     * Retrieves a list of transactions.
     *
     * @param {object} filter
     *
     * @returns {Promise<MellonWebECRTransactionListResponse>}
     */
    async getTransactions(filter = {}) {
        const queryParams = new URLSearchParams(filter);
        return this._request('GET', `/api/v2.1/transaction/?${queryParams}`);
    }

    /**
     * Retrieves details of a transaction.
     *
     * @param {string} transactionId
     *
     * @returns {Promise<MellonWebECRTransaction>}
     */
    async getTransaction(transactionId) {
        return this._request('GET', `/api/v2.1/transaction/${transactionId}/`);
    }

    /**
     * Binds a terminal to an ECR.
     *
     * @param {string} terminalId
     * @param {MellonWebECREcrTerminalBindRequest} request
     *
     * @returns {Promise<MellonWebECREcrTerminalBindResponse>}
     */
    async bindTerminal(terminalId, request) {
        return this._request('POST', `/api/v2.1/terminal/${terminalId}/ecrbind/`, request);
    }

    /**
     * Exchanges session keys with an ECR.
     *
     * @param {string} terminalId
     * @param {MellonWebECRKeyExchangeRequest} request
     *
     * @returns {Promise<MellonWebECRKeyExchangeResponse>}
     */
    async ecrKeyExchange(terminalId, request) {
        return this._request('POST', `/api/v2.1/terminal/${terminalId}/ecrkeyexchange/`, request);
    }

    /**
     * Aborts all currently active requests.
     *
     * @returns {boolean} True if any requests were aborted, false otherwise.
     */
    abortRequests() {
        let abortedCount = 0;
        this.activeRequests.forEach((controller, requestId) => {
            this.log.debug('Aborting request', requestId);
            controller.abort();
            abortedCount++;
        });
        this.activeRequests.clear();
        return abortedCount > 0;
    }
}

MellonWebECRClient.API_KEY_HEADER = 'X-Api-Key';

module.exports = MellonWebECRClient;
